/**
 * The canteen week planner's rules (parent app and parent web portal), kept pure so they can be
 * tested without rendering anything. The server (api/canteen/my/{studentId}/planner) enforces every
 * rule again; these only decide what the screen offers.
 *
 *  * a slot (one meal on one day) takes at most ONE option - lunch option 1 OR 2;
 *  * a slot can be chosen only when its day is orderable, it is still open (before its cut-off),
 *    and nothing is ordered for it yet;
 *  * an option with a blockedReason (allergy, not priced) or no price can't be chosen;
 *  * the week's total is only a preview - the server prices every choice itself - and it is paid
 *    from the child's wallet only, so a total above the balance sends the parent to top up first;
 *  * extras (a la carte drinks, beverages, snacks) are counted per day and item: up to 5 of one
 *    item a day, already ordered ones included, only while the day's extras are open.
 */

import type {
  CanteenOrder,
  CanteenPlanner,
  CanteenPlannerDay,
  CanteenPlannerExtrasOrder,
  CanteenPlannerOption,
  CanteenPlannerOrdered,
  CanteenPlannerSlot,
  CanteenPlanRequest,
} from '../types/wallet';

/** The parent's choices: `${date}|${slotId}` -> optionNo. */
export type PlanSelection = Record<string, number>;

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const slotKey = (date: string, slotId: string) => `${date}|${slotId}`;

/** The parent's extras: `${date}|${itemId}` -> quantity (1..5). */
export type ExtrasSelection = Record<string, number>;

/** The most of one extra a day may have, already ordered included. */
export const MAX_EXTRA_PER_DAY = 5;

export const extraKey = (date: string, itemId: string) => `${date}|${itemId}`;

/** YYYY-MM-DD of a local date. */
export const isoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** A YYYY-MM-DD string as a local date (no time-zone shift). */
export function parseIsoDate(value: string): Date {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** The Monday of the week `d` falls in (Sunday belongs to the week before). */
export function mondayOf(d: Date): Date {
  const back = (d.getDay() + 6) % 7;
  return addDays(d, -back);
}

/** `from` (a Monday) of this week, next week or the week after. */
export function weekFrom(today: Date, offset: number): string {
  return isoDate(addDays(mondayOf(today), offset * 7));
}

/** The Monday seven days before `from`. */
export const previousWeek = (from: string) => isoDate(addDays(parseIsoDate(from), -7));

/** "26–30 Oct" (Mon..Fri), "29 Sep – 3 Oct" across months. */
export function weekRangeLabel(from: string): string {
  const start = parseIsoDate(from);
  const end = addDays(start, 4);
  return start.getMonth() === end.getMonth()
    ? `${start.getDate()}–${end.getDate()} ${MONTHS[end.getMonth()]}`
    : `${start.getDate()} ${MONTHS[start.getMonth()]} – ${end.getDate()} ${MONTHS[end.getMonth()]}`;
}

/** This week / Next week / Week after, each with its Monday. */
export function weekChoices(today: Date): { value: string; label: string }[] {
  return ['This week', 'Next week', 'Week after'].map((name, i) => {
    const from = weekFrom(today, i);
    return { value: from, label: `${name} · ${weekRangeLabel(from)}` };
  });
}

/** "Mon 26 Oct". */
export function dayLabel(date: string): string {
  const d = parseIsoDate(date);
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** One line of the "Order placed" popup. */
export interface PlacedLine { key: string; when: string; item: string; orderNumber: string; amount: number }

/**
 * What the "Order placed" popup shows after Place week (bug, 27 Oct 2026: a 3-second toast was
 * easy to miss): each order in date order with its meal, dish and number, and the days it covers.
 */
export function placedSummary(placed: { date: string; slotName: string; canteenOrderId: string; orderNumber: string; itemName: string; amount: number }[]): {
  span: string;
  lines: PlacedLine[];
} {
  const sorted = [...placed].sort((a, b) => a.date.localeCompare(b.date));
  const days = Array.from(new Set(sorted.map(p => p.date)));
  const span = days.length === 0 ? '' : days.length === 1
    ? `on ${dayLabel(days[0])}`
    : `${dayLabel(days[0])} – ${dayLabel(days[days.length - 1])}`;
  return {
    span,
    lines: sorted.map(p => ({
      key: p.canteenOrderId, when: `${dayLabel(p.date)} · ${p.slotName}`, item: p.itemName, orderNumber: p.orderNumber, amount: p.amount,
    })),
  };
}

export interface PlacedTotals {
  items: number;
  subtotal: number;
  /** subtotal − what the wallet was charged, when the school took something off; else 0. */
  discount: number;
  /** What the wallet was charged - the server's figure, the same one the receipt shows. */
  total: number;
  walletBefore: number;
  walletAfter: number;
}

/**
 * The money summary of the "Order placed" popup. The total is the server's charge (it is
 * what left the wallet and what the receipt and order history show); the subtotal is the
 * sum of the items listed, and any gap between them is shown as the discount. The balance
 * before is the balance after plus that charge.
 */
export function placedTotals(result: { placed?: { amount: number }[]; total: number; walletBalanceAfter: number }): PlacedTotals {
  const cents = (n: number) => Math.round((Number(n) || 0) * 100);
  const lines = result.placed ?? [];
  const subtotalC = lines.reduce((s, l) => s + cents(l.amount), 0);
  const totalC = cents(result.total);
  const afterC = cents(result.walletBalanceAfter);
  return {
    items: lines.length,
    subtotal: subtotalC / 100,
    discount: Math.max(0, subtotalC - totalC) / 100,
    total: totalC / 100,
    walletBefore: (afterC + totalC) / 100,
    walletAfter: afterC / 100,
  };
}

/** "For Mon 2 Nov · Lunch" - on a placed order, so it says which day it is for wherever it is scrolled to. */
export function servedForLabel(date: string, meal: string): string {
  return `For ${dayLabel(date)} · ${meal}`;
}

/** "Menu week 2": the menu's rotation week (1-4), not a calendar week; empty when the school has no rotation. */
export function menuWeekLabel(cycleWeek?: number | null): string {
  return cycleWeek ? `Menu week ${cycleWeek}` : '';
}

/** "Mon". */
export const shortDayName = (date: string) => DAY_NAMES[parseIsoDate(date).getDay()];

const isWeekend = (date: string) => {
  const day = parseIsoDate(date).getDay();
  return day === 0 || day === 6;
};

/** The days the planner shows: Saturday and Sunday are hidden when they have no menu. */
export function visibleDays(planner: Pick<CanteenPlanner, 'days'> | null): CanteenPlannerDay[] {
  if (!planner) { return []; }
  return planner.days.filter(d => !(isWeekend(d.date) && (d.closedReason === 'No menu' || d.slots.length === 0)));
}

/** A slot can take a choice: its day is orderable, it is before its cut-off, and nothing is ordered. */
export function isSlotChoosable(day: Pick<CanteenPlannerDay, 'isOrderable'>, slot: Pick<CanteenPlannerSlot, 'isOpen' | 'ordered'>): boolean {
  return day.isOrderable && slot.isOpen && !slot.ordered;
}

/** An option can be chosen: it is not blocked and it has a price for this child. */
export function isOptionChoosable(option: Pick<CanteenPlannerOption, 'blockedReason' | 'price'>): boolean {
  return !option.blockedReason && option.price !== null && option.price !== undefined;
}

/** A blocked reason that is about an allergy (shown red). */
export const isAllergyReason = (reason: string | null | undefined) => !!reason && /allerg/i.test(reason);

/** Chooses an option for a slot, or un-chooses it when it was already the choice. One option per slot. */
export function toggleChoice(selection: PlanSelection, date: string, slotId: string, optionNo: number): PlanSelection {
  const key = slotKey(date, slotId);
  const next = { ...selection };
  if (next[key] === optionNo) { delete next[key]; } else { next[key] = optionNo; }
  return next;
}

interface Located { day: CanteenPlannerDay; slot: CanteenPlannerSlot; option: CanteenPlannerOption }

/** Every choice that still stands against this planner, with its day, slot and option. */
function located(selection: PlanSelection, planner: Pick<CanteenPlanner, 'days'> | null): Located[] {
  if (!planner) { return []; }
  const found: Located[] = [];
  planner.days.forEach(day => day.slots.forEach(slot => {
    const optionNo = selection[slotKey(day.date, slot.slotId)];
    if (optionNo === undefined || !isSlotChoosable(day, slot)) { return; }
    const option = slot.options.find(o => o.optionNo === optionNo);
    if (option && isOptionChoosable(option)) { found.push({ day, slot, option }); }
  }));
  return found;
}

/** Drops choices that can no longer be made (after a reload: ordered, past cut-off, blocked). */
export function pruneSelection(selection: PlanSelection, planner: Pick<CanteenPlanner, 'days'> | null): PlanSelection {
  const kept: PlanSelection = {};
  located(selection, planner).forEach(({ day, slot, option }) => { kept[slotKey(day.date, slot.slotId)] = option.optionNo; });
  return kept;
}

/** How many slots are chosen. */
export const choiceCount = (selection: PlanSelection, planner: Pick<CanteenPlanner, 'days'> | null) =>
  located(selection, planner).length;

/** The chosen total (meals and extras) in cents-safe arithmetic. */
export function selectionTotal(
  selection: PlanSelection,
  planner: Pick<CanteenPlanner, 'days'> | null,
  extras: ExtrasSelection = {},
): number {
  const meals = located(selection, planner).reduce((sum, { option }) => sum + Math.round((option.price ?? 0) * 100), 0);
  const added = locatedExtras(extras, planner)
    .reduce((sum, { item, quantity }) => sum + Math.round((item.price ?? 0) * 100) * quantity, 0);
  return (meals + added) / 100;
}

/** A meal Same lunch / Copy last week could not choose, and why: Wed, Lunch, "past its cut-off". */
export interface SkippedMeal { date: string; slotName: string; reason: string }

/**
 * What Same lunch every day / Copy last week would do: the new selection, how many meals it
 * chose, how many of the parent's own choices it changes (asked before applying), and what it
 * could not choose.
 */
export interface CopyResult { selection: PlanSelection; copied: number; replaced: number; skipped: SkippedMeal[] }

type CopyPlanner = Pick<CanteenPlanner, 'days'> & Partial<Pick<CanteenPlanner, 'today'>>;

/**
 * Why a slot can't take a copied dish, or null when it can. Days already gone (before today) are
 * not worth a word, so they come back as '' and are left out quietly.
 */
function slotUnavailable(day: CanteenPlannerDay, slot: CanteenPlannerSlot, today?: string): string | null {
  if (day.isOrderable && slot.isOpen) { return null; }
  if (today && day.date < today) { return ''; }
  return day.isOrderable ? 'past its cut-off' : (day.closedReason?.trim() || 'the canteen is closed');
}

/** Why an option can't be chosen, or null when it can. */
function optionUnavailable(option: CanteenPlannerOption | undefined): string | null {
  if (!option) { return 'not on the menu that day'; }
  if (option.blockedReason) { return option.blockedReason.trim().replace(/\.$/, ''); }
  if (option.price === null || option.price === undefined) { return 'not offered at this price'; }
  return null;
}

/** Chooses `optionNo` for one slot, counting it and whether it changed a choice already made. */
function choose(result: CopyResult, before: PlanSelection, key: string, optionNo: number) {
  if (before[key] !== undefined && before[key] !== optionNo) { result.replaced++; }
  result.selection[key] = optionNo;
  result.copied++;
}

/** Notes a slot that could not be chosen; days already gone ('') are left out. */
function skip(result: CopyResult, day: CanteenPlannerDay, slot: CanteenPlannerSlot, reason: string) {
  if (reason) { result.skipped.push({ date: day.date, slotName: slot.name, reason }); }
}

const isLunch = (slot: Pick<CanteenPlannerSlot, 'name'>) => /lunch/i.test(slot.name);

/** The option a past order was for: its optionNo, else the option serving the same dish. */
function orderedOptionNo(ordered: CanteenPlannerOrdered, slot: CanteenPlannerSlot): number | null {
  if (ordered.optionNo !== null && ordered.optionNo !== undefined) { return ordered.optionNo; }
  return slot.options.find(o => o.itemId === ordered.itemId)?.optionNo ?? null;
}

/** The lunch Same lunch every day repeats: the one chosen on `preferDate`, else the first chosen, else the first ordered. */
function sourceLunch(selection: PlanSelection, days: CanteenPlannerDay[], preferDate?: string) {
  const inOrder = [...days.filter(d => d.date === preferDate), ...days.filter(d => d.date !== preferDate)];
  for (const day of inOrder) {
    for (const slot of day.slots.filter(isLunch)) {
      const option = slot.options.find(o => o.optionNo === selection[slotKey(day.date, slot.slotId)]);
      if (option) { return { date: day.date, slotId: slot.slotId, itemId: option.itemId, optionNo: option.optionNo, name: option.name }; }
    }
  }
  for (const day of inOrder) {
    for (const slot of day.slots.filter(isLunch)) {
      if (slot.ordered) {
        return { date: day.date, slotId: slot.slotId, itemId: slot.ordered.itemId, optionNo: orderedOptionNo(slot.ordered, slot), name: slot.ordered.itemName };
      }
    }
  }
  return null;
}

/**
 * "Same lunch every day": the lunch the parent chose (on the day shown, else the first day with a
 * lunch chosen, else the first lunch already ordered) is chosen for every other open lunch of the
 * week - the same dish where it is on the menu, else the same option number. Lunches already ordered
 * are left alone; ones that can't take it are listed. `source` is null when no lunch is chosen yet.
 */
export function sameLunchEveryDay(
  selection: PlanSelection,
  planner: CopyPlanner | null,
  preferDate?: string,
): CopyResult & { source: { date: string; name: string } | null } {
  const result: CopyResult = { selection: { ...selection }, copied: 0, replaced: 0, skipped: [] };
  const days = visibleDays(planner);
  const source = sourceLunch(selection, days, preferDate);
  if (!source) { return { ...result, source: null }; }
  days.forEach(day => day.slots.filter(isLunch).forEach(slot => {
    if (slot.ordered || (day.date === source.date && slot.slotId === source.slotId)) { return; }
    const closed = slotUnavailable(day, slot, planner?.today);
    if (closed !== null) { skip(result, day, slot, closed); return; }
    const option = slot.options.find(o => o.itemId === source.itemId)
      ?? slot.options.find(o => o.optionNo === source.optionNo);
    const blocked = optionUnavailable(option);
    if (blocked !== null || !option) { skip(result, day, slot, blocked ?? ''); return; }
    choose(result, selection, slotKey(day.date, slot.slotId), option.optionNo);
  }));
  return { ...result, source: { date: source.date, name: source.name } };
}

/** Any meal ordered in this planner's week (what Copy last week can copy from). */
export const hasOrderedMeals = (planner: Pick<CanteenPlanner, 'days'> | null) =>
  (planner?.days ?? []).some(day => day.slots.some(slot => !!slot.ordered));

/**
 * "Copy last week": an earlier week's ordered option per weekday and slot, chosen this week where
 * the slot is open and that option can be chosen. Slots already ordered this week are left alone;
 * ones that can't take it are listed.
 */
export function copyLastWeek(
  selection: PlanSelection,
  planner: CopyPlanner | null,
  lastWeek: Pick<CanteenPlanner, 'days'> | null,
): CopyResult {
  const past = new Map<string, { optionNo: number | null; itemId: string }>();
  (lastWeek?.days ?? []).forEach(day => day.slots.forEach(slot => {
    if (!slot.ordered) { return; }
    past.set(`${parseIsoDate(day.date).getDay()}|${slot.slotId}`,
      { optionNo: orderedOptionNo(slot.ordered, slot), itemId: slot.ordered.itemId });
  }));
  const result: CopyResult = { selection: { ...selection }, copied: 0, replaced: 0, skipped: [] };
  (planner?.days ?? []).forEach(day => day.slots.forEach(slot => {
    const was = past.get(`${parseIsoDate(day.date).getDay()}|${slot.slotId}`);
    if (!was || slot.ordered) { return; }
    const closed = slotUnavailable(day, slot, planner?.today);
    if (closed !== null) { skip(result, day, slot, closed); return; }
    const option = (was.optionNo !== null ? slot.options.find(o => o.optionNo === was.optionNo) : undefined)
      ?? slot.options.find(o => o.itemId === was.itemId);
    const blocked = optionUnavailable(option);
    if (blocked !== null || !option) { skip(result, day, slot, blocked ?? ''); return; }
    choose(result, selection, slotKey(day.date, slot.slotId), option.optionNo);
  }));
  return result;
}

/** "Wed lunch (past its cut-off), Fri breakfast (not on the menu that day)" - at most four, then "and 2 more". */
export function skippedText(skipped: SkippedMeal[]): string {
  const shown = skipped.slice(0, 4).map(s => `${shortDayName(s.date)} ${s.slotName.toLowerCase()} (${s.reason})`);
  const more = skipped.length - shown.length;
  return more > 0 ? `${shown.join(', ')} and ${more} more` : shown.join(', ');
}

/** The POST body: (date, slot, option) and (date, item, quantity) only, in day order - the server prices it. */
export function toPlanRequest(
  selection: PlanSelection,
  planner: Pick<CanteenPlanner, 'days'> | null,
  extras: ExtrasSelection = {},
): CanteenPlanRequest {
  return {
    choices: located(selection, planner).map(({ day, slot, option }) => ({ date: day.date, slotId: slot.slotId, optionNo: option.optionNo })),
    extras: locatedExtras(extras, planner).map(({ day, item, quantity }) => ({ date: day.date, itemId: item.itemId, quantity })),
  };
}

export type PlaceBlock = 'EMPTY' | 'CLOSED' | 'NO_WALLET' | 'FROZEN' | 'OVER_BALANCE' | null;

/** Why Place week is not offered right now, or null when it can be placed. */
export function placeBlock(
  selection: PlanSelection,
  planner: Pick<CanteenPlanner, 'days' | 'isOrderingOpen' | 'walletsEnabled' | 'walletId' | 'walletStatus' | 'walletBalance'> | null,
  extras: ExtrasSelection = {},
): PlaceBlock {
  if (!planner || (choiceCount(selection, planner) === 0 && extrasCount(extras, planner) === 0)) { return 'EMPTY'; }
  if (!planner.isOrderingOpen) { return 'CLOSED'; }
  if (!planner.walletsEnabled || !planner.walletId) { return 'NO_WALLET'; }
  if (planner.walletStatus && planner.walletStatus !== 'Active') { return 'FROZEN'; }
  if (selectionTotal(selection, planner, extras) > planner.walletBalance + 1e-9) { return 'OVER_BALANCE'; }
  return null;
}

/** The wallet bar: how far the week's total is over the balance (0 when it fits) and what is left. */
export function walletShortfall(total: number, balance: number): { shortBy: number; left: number } {
  const diff = Math.round(total * 100) - Math.round(balance * 100);
  return { shortBy: Math.max(0, diff) / 100, left: Math.max(0, -diff) / 100 };
}

/** Who placed an order already in the planner: "Ordered by Ms Ebanks", "Ordered at the counter". */
export function orderedByLabel(ordered: Pick<CanteenPlannerOrdered, 'placedVia' | 'orderedBy'>): string {
  const who = ordered.orderedBy?.trim();
  if (who) { return `Ordered by ${who}`; }
  switch ((ordered.placedVia ?? '').toUpperCase()) {
    case 'PARENT': return 'Ordered by family';
    case 'TEACHER': return 'Ordered by the class teacher';
    case 'STAFF': return 'Ordered by school staff';
    case 'COUNTER': return 'Ordered at the counter';
    default: return 'Ordered';
  }
}

/** The small line on a placed order: "Order ORD-001 · Ordered by Parent" - the order's own number first (bug, 27 Oct 2026). */
export function orderInfoLine(ordered: Pick<CanteenPlannerOrdered, 'orderNumber' | 'placedVia' | 'orderedBy'>): string {
  const number = ordered.orderNumber?.trim();
  return [number ? `Order ${number}` : null, orderedByLabel(ordered)].filter(Boolean).join(' · ');
}

export interface SlotRow { slotId: string; name: string; startTime: string | null }

/** The meal rows of the week grid: every slot seen on a shown day, in serving-time order. */
export function slotRows(days: CanteenPlannerDay[]): SlotRow[] {
  const rows = new Map<string, SlotRow>();
  days.forEach(day => day.slots.forEach(slot => {
    if (!rows.has(slot.slotId)) { rows.set(slot.slotId, { slotId: slot.slotId, name: slot.name, startTime: slot.startTime ?? null }); }
  }));
  return Array.from(rows.values()).sort((a, b) => (a.startTime ?? '99').localeCompare(b.startTime ?? '99'));
}

/** The line under a slot: "Order by 8:00 pm Wed 14 Oct" or "Closed · cut-off was ...". */
export function cutOffText(slot: Pick<CanteenPlannerSlot, 'isOpen' | 'cutOffLabel'>): string {
  return slot.isOpen ? `Order by ${slot.cutOffLabel}` : `Closed · cut-off was ${slot.cutOffLabel}`;
}

// ── Extras (a la carte drinks, beverages, snacks) ─────────────────────────────────────────────

const isLiveOrder = (order: Pick<CanteenPlannerExtrasOrder, 'status'>) =>
  !/^(CANCELLED|REJECTED)$/i.test(order.status ?? '');

/** How many of one extra are already ordered for the day (cancelled and rejected orders don't count). */
export function extraOrderedQty(day: Pick<CanteenPlannerDay, 'extrasOrdered'>, itemId: string): number {
  return (day.extrasOrdered ?? []).filter(isLiveOrder).reduce((sum, order) =>
    sum + (order.lines ?? []).filter(l => l.itemId === itemId).reduce((n, l) => n + (l.quantity || 0), 0), 0);
}

/** The day's extras can be added to: the day is orderable and before its extras cut-off. */
export const areExtrasOpen = (day: Pick<CanteenPlannerDay, 'isOrderable' | 'extrasOpen'>) => day.isOrderable && !!day.extrasOpen;

/** The most of this extra that can still be added for the day: 0..5 minus what is already ordered. */
export function extraMax(
  day: Pick<CanteenPlannerDay, 'isOrderable' | 'extrasOpen' | 'extrasOrdered'>,
  item: Pick<CanteenPlannerOption, 'itemId' | 'blockedReason' | 'price'>,
): number {
  if (!areExtrasOpen(day) || !isOptionChoosable(item)) { return 0; }
  return Math.max(0, MAX_EXTRA_PER_DAY - extraOrderedQty(day, item.itemId));
}

/** One more or one fewer of an extra for a day, kept within 0..extraMax; 0 drops it. */
export function stepExtra(
  extras: ExtrasSelection,
  day: Pick<CanteenPlannerDay, 'date' | 'isOrderable' | 'extrasOpen' | 'extrasOrdered'>,
  item: Pick<CanteenPlannerOption, 'itemId' | 'blockedReason' | 'price'>,
  delta: number,
): ExtrasSelection {
  const key = extraKey(day.date, item.itemId);
  const quantity = Math.min(extraMax(day, item), Math.max(0, (extras[key] ?? 0) + delta));
  const next = { ...extras };
  if (quantity > 0) { next[key] = quantity; } else { delete next[key]; }
  return next;
}

interface LocatedExtra { day: CanteenPlannerDay; item: CanteenPlannerOption; quantity: number }

/** Every extra that still stands against this planner, its quantity cut to what the day still allows. */
function locatedExtras(extras: ExtrasSelection, planner: Pick<CanteenPlanner, 'days'> | null): LocatedExtra[] {
  if (!planner) { return []; }
  const found: LocatedExtra[] = [];
  planner.days.forEach(day => (day.extras ?? []).forEach(item => {
    const wanted = extras[extraKey(day.date, item.itemId)];
    if (!wanted) { return; }
    const quantity = Math.min(Math.floor(wanted), extraMax(day, item));
    if (quantity > 0) { found.push({ day, item, quantity }); }
  }));
  return found;
}

/** Drops extras that can no longer be added (after a reload: past cut-off, blocked, already 5 ordered). */
export function pruneExtras(extras: ExtrasSelection, planner: Pick<CanteenPlanner, 'days'> | null): ExtrasSelection {
  const kept: ExtrasSelection = {};
  locatedExtras(extras, planner).forEach(({ day, item, quantity }) => { kept[extraKey(day.date, item.itemId)] = quantity; });
  return kept;
}

/** How many extras are chosen (units, not lines). */
export const extrasCount = (extras: ExtrasSelection, planner: Pick<CanteenPlanner, 'days'> | null) =>
  locatedExtras(extras, planner).reduce((sum, { quantity }) => sum + quantity, 0);

/** Something is chosen or ordered on the day (meals or extras) - the phone's day-tab dot. */
/**
 * Whether a planner day is today, by the server's `today` (the school's date, not the
 * device's). The week tabs had no today marker at all, so their "meal ordered" dot was
 * read as one (QA: "today is Wednesday but the dot is under Monday").
 */
export function isPlannerToday(dayDate: string, today: string | null | undefined): boolean {
  return !!today && !!dayDate && dayDate.slice(0, 10) === today.slice(0, 10);
}

export function dayHasActivity(day: CanteenPlannerDay, selection: PlanSelection, extras: ExtrasSelection): boolean {
  return day.slots.some(sl => selection[slotKey(day.date, sl.slotId)] !== undefined || !!sl.ordered)
    || (day.extras ?? []).some(item => (extras[extraKey(day.date, item.itemId)] ?? 0) > 0)
    || (day.extrasOrdered ?? []).some(isLiveOrder);
}

/** "2 × Orange juice, 1 × Water bottle". */
export const extrasOrderText = (order: Pick<CanteenPlannerExtrasOrder, 'lines'>) =>
  (order.lines ?? []).map(l => `${l.quantity} × ${l.itemName}`).join(', ');

/** The line on a day's Extras block: "Order extras until 9:00 am Mon 26 Oct", or closed with the cut-off. */
export function extrasCutOffText(day: Pick<CanteenPlannerDay, 'isOrderable' | 'extrasOpen' | 'extrasCutOffLabel'>): string {
  if (areExtrasOpen(day)) { return day.extrasCutOffLabel ? `Order extras until ${day.extrasCutOffLabel}` : 'Open for orders'; }
  return day.extrasCutOffLabel ? `Closed · cut-off was ${day.extrasCutOffLabel}` : 'Closed';
}

// ── Order history (27 Oct 2026: a month of a child's orders on the Canteen screen) ───────────────

/** This month and the five before it: "2026-10" / "Oct 2026", newest first. */
export function monthChoices(today: Date, count = 6): { value: string; label: string }[] {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    return { value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` };
  });
}

const isVoid = (o: Pick<CanteenOrder, 'orderStatus'>) => /^(CANCELLED|REJECTED)$/i.test(o.orderStatus ?? '');

/** The day an order's food is for: its serve date, else the day it was ordered. */
const servedOn = (o: Pick<CanteenOrder, 'serveDate' | 'orderDate'>) => (o.serveDate || o.orderDate || '').slice(0, 10);

/** When the order was placed, in ms; an order without one sorts last. */
const placedAt = (o: Pick<CanteenOrder, 'orderDate'>) => {
  const t = Date.parse(o.orderDate || '');
  return Number.isNaN(t) ? 0 : t;
};

/** The most recently ordered first; two placed at the same moment, the higher order number first. */
const newestOrderFirst = (a: CanteenOrder, b: CanteenOrder) =>
  placedAt(b) - placedAt(a) || b.orderNumber.localeCompare(a.orderNumber, undefined, { numeric: true });

export interface OrderHistoryView {
  summary: { orders: number; spent: number; cancelled: number };
  /** Orders for a day after today, the most recently ordered first (parents asked, 2 Nov 2026). */
  upcoming: CanteenOrder[];
  /** Today and before, one group per day, newest day first; in a day, the most recently ordered first. */
  days: { date: string; label: string; orders: CanteenOrder[] }[];
}

/**
 * A month's orders laid out for the screen: a summary (spent leaves out cancelled and rejected
 * orders - that money went back to the wallet), upcoming orders first, then day by day.
 */
export function orderHistoryView(orders: CanteenOrder[], todayIso: string): OrderHistoryView {
  const live = orders.filter(o => !isVoid(o));
  const summary = {
    orders: orders.length,
    spent: Math.round(live.reduce((t, o) => t + (o.totalAmount || 0), 0) * 100) / 100,
    cancelled: orders.length - live.length,
  };
  const upcoming = orders.filter(o => servedOn(o) > todayIso).sort(newestOrderFirst);
  const past = orders.filter(o => servedOn(o) <= todayIso);
  const byDay = new Map<string, CanteenOrder[]>();
  for (const o of past) {
    const day = servedOn(o);
    byDay.set(day, [...(byDay.get(day) ?? []), o]);
  }
  const days = Array.from(byDay.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, list]) => ({
      date,
      label: date ? dayLabel(date) : 'Date not known',
      orders: list.sort(newestOrderFirst),
    }));
  return { summary, upcoming, days };
}

/** "4 × tea, 5 × test" - an order's items on one line. */
export function orderItemsText(o: Pick<CanteenOrder, 'items'>): string {
  return (o.items ?? []).map(i => `${i.quantity} × ${i.itemName}`).join(', ');
}

/** Whether an order was cancelled or rejected (shown faded, its money back in the wallet). */
export const isVoidOrder = isVoid;

/** One line of the order review: a meal or an extra, with its day, slot, quantity and price. */
export interface ReviewLine {
  key: string;
  date: string;
  /** "Tue 3 Nov". */
  day: string;
  /** The meal slot ("Breakfast") or "Extras". */
  slot: string;
  item: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  kind: 'meal' | 'extra';
}

export interface OrderReview {
  lines: ReviewLine[];
  meals: number;
  extras: number;
  /** What the wallet is charged for all of it. */
  total: number;
  walletBefore: number;
  walletAfter: number;
}

/**
 * What Confirm order would place, before any money moves (QA, 31 Oct 2026: "Place week" used
 * to order at once, with no way to check the copied or chosen meals first). Day by day, the
 * meals in slot order and then that day's extras; the same choices toPlanRequest sends.
 */
export function orderReview(
  selection: PlanSelection,
  planner: Pick<CanteenPlanner, 'days' | 'walletBalance'> | null,
  extras: ExtrasSelection = {},
): OrderReview {
  const cents = (n: number) => Math.round(n * 100);
  const lines: ReviewLine[] = [];
  const meals = located(selection, planner);
  const added = locatedExtras(extras, planner);
  (planner?.days ?? []).forEach(d => {
    meals.filter(m => m.day.date === d.date).forEach(({ day, slot, option }) => {
      const price = option.price ?? 0;
      lines.push({ key: slotKey(day.date, slot.slotId), date: day.date, day: dayLabel(day.date), slot: slot.name,
        item: option.name, quantity: 1, unitPrice: price, amount: price, kind: 'meal' });
    });
    added.filter(e => e.day.date === d.date).forEach(({ day, item, quantity }) => {
      const price = item.price ?? 0;
      lines.push({ key: extraKey(day.date, item.itemId), date: day.date, day: dayLabel(day.date), slot: 'Extras',
        item: item.name, quantity, unitPrice: price, amount: (cents(price) * quantity) / 100, kind: 'extra' });
    });
  });
  const total = lines.reduce((sum, l) => sum + cents(l.amount), 0) / 100;
  const walletBefore = planner?.walletBalance ?? 0;
  return {
    lines,
    meals: meals.length,
    extras: added.reduce((sum, e) => sum + e.quantity, 0),
    total,
    walletBefore,
    walletAfter: (cents(walletBefore) - cents(total)) / 100,
  };
}

/** Only one day's choices - Order for today keeps the rest of the week's picks out of the order. */
export function onlyDay<T extends Record<string, number>>(chosen: T, date: string): T {
  const kept = {} as Record<string, number>;
  Object.entries(chosen).forEach(([key, value]) => { if (key.startsWith(`${date}|`)) { kept[key] = value; } });
  return kept as T;
}

// ── Order by day and the menu (owner, 2 Nov 2026) ─────────────────────────────────────────────

/** What an order for one day is called: "today's order", or "the order for Tue 3 Nov". */
export function dayOrderName(date: string, today: string | null | undefined): string {
  return isPlannerToday(date, today) ? "today's order" : `the order for ${dayLabel(date)}`;
}

/** "12:00–13:00" from a meal's serving times; '' when the school gave none. */
export function slotTimeText(slot: Pick<CanteenPlannerSlot, 'startTime' | 'endTime'>): string {
  const start = (slot.startTime ?? '').slice(0, 5);
  const end = (slot.endTime ?? '').slice(0, 5);
  return start && end ? `${start}–${end}` : start;
}

/** "Contains nuts, dairy" from a dish's allergen codes (NUTS, DAIRY ...); '' when it has none. */
export function allergenText(codes: string[] | null | undefined): string {
  const names = (codes ?? []).filter(Boolean).map(c => c.toLowerCase());
  return names.length > 0 ? `Contains ${names.join(', ')}` : '';
}

/**
 * The menu's line under a meal: when it can be ordered until, or that ordering has closed. Empty on a
 * day that can't be ordered at all (past, holiday, too far ahead) - the day says why instead.
 */
export function menuOrderText(day: Pick<CanteenPlannerDay, 'isOrderable'>, slot: Pick<CanteenPlannerSlot, 'isOpen' | 'cutOffLabel'>): string {
  if (!day.isOrderable) { return ''; }
  return slot.isOpen ? `Order by ${slot.cutOffLabel}` : `Ordering closed at ${slot.cutOffLabel}`;
}

/**
 * What changing a dish does to the wallet (owner, 2 Nov 2026): the paid amount comes back and the new dish is
 * charged at today's price - "more", "back" or "same", and by how much.
 */
export function changeDifference(paid: number, price: number): { direction: 'more' | 'back' | 'same'; amount: number } {
  const cents = Math.round(price * 100) - Math.round(paid * 100);
  if (cents === 0) { return { direction: 'same', amount: 0 }; }
  return { direction: cents > 0 ? 'more' : 'back', amount: Math.abs(cents) / 100 };
}

/** The food allergies the family wrote at registration, not yet confirmed by the school nurse (they warn, never block). */
export function declaredAllergies(planner: Pick<CanteenPlanner, 'allergies'> | null): string[] {
  return (planner?.allergies ?? []).filter(a => a.flagType === 'DECLARED').map(a => a.name);
}

/** The school's same-day limit for the parent: "Orders for the same day close at 8:00 am at the latest." */
export function sameDayCutOffNote(planner: Pick<CanteenPlanner, 'sameDayCutOffText'> | null): string {
  return planner?.sameDayCutOffText ? `Orders for the same day close at ${planner.sameDayCutOffText} at the latest.` : '';
}
